import React, { useEffect, useState } from 'react';
import { X, FileText, Loader2, Sparkles, Building2 } from 'lucide-react';

const inputClass = "w-full px-3 py-2 border border-[#D8DDE6] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";

/**
 * Phase 10 — lets an admin start a new form from a starter template
 * (platform-wide, shipped in code — see builtInTemplates.js) or one of
 * their own previously saved templates, rather than always starting
 * from a blank page. Picking one just clones its schema into a brand
 * new Form; the template itself is never touched.
 */
export default function TemplateGalleryModal({ onClose, onFetchTemplates, onCreateFromTemplate }) {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState({ builtIn: [], own: [] });
  const [selected, setSelected] = useState(null);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    onFetchTemplates()
      .then(setTemplates)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectTemplate = (template) => {
    setSelected(template);
    setName(template.name);
  };

  const create = async () => {
    if (!selected || !name.trim()) return;
    setCreating(true);
    try {
      await onCreateFromTemplate({
        builtInKey: selected.source === 'built-in' ? selected.key : undefined,
        templateId: selected.source === 'own' ? selected.id : undefined,
        name: name.trim(),
      });
    } finally {
      setCreating(false);
    }
  };

  const TemplateCard = ({ template, icon: Icon }) => (
    <button
      onClick={() => selectTemplate(template)}
      className={`w-full text-left flex items-start gap-3 p-3 rounded-[4px] border transition-colors ${
        selected === template ? 'border-[#0176D3] bg-[#0176D3]/5' : 'border-[#D8DDE6] hover:border-[#0176D3]/40'
      }`}
    >
      <Icon size={16} className="text-[#0176D3] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-[#181818] truncate">{template.name}</p>
        {template.description && <p className="text-[11px] text-[#706E6B] mt-0.5">{template.description}</p>}
      </div>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="w-full max-w-[540px] max-h-[100dvh] overflow-y-auto bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 mb-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <p className="text-[16px] font-bold text-[#2E2E2E]">New Form from Template</p>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#706E6B]">
            <Loader2 size={18} className="animate-spin mr-2" /> Loading templates…
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <p className="text-[11px] font-bold text-[#706E6B] mb-2 flex items-center gap-1.5">
                <Sparkles size={12} /> Starter Templates
              </p>
              <div className="space-y-2">
                {templates.builtIn.map((t) => <TemplateCard key={t.key} template={t} icon={FileText} />)}
              </div>
            </div>

            {templates.own.length > 0 && (
              <div>
                <p className="text-[11px] font-bold text-[#706E6B] mb-2 flex items-center gap-1.5">
                  <Building2 size={12} /> Your Templates
                </p>
                <div className="space-y-2">
                  {templates.own.map((t) => <TemplateCard key={t.id} template={t} icon={Building2} />)}
                </div>
              </div>
            )}

            {selected && (
              <div className="pt-4 border-t border-[#F0F1F3]">
                <label className="text-[11px] font-semibold text-[#706E6B] block mb-1.5">Form Name</label>
                <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
                <button
                  onClick={create}
                  disabled={!name.trim() || creating}
                  className="mt-3 w-full px-4 h-9 rounded bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold disabled:opacity-60 transition-colors"
                >
                  {creating ? 'Creating…' : 'Create Form'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
